import React, { useState } from "react";
import './Alf.scss';
import { Helmet } from "react-helmet";
import emailjs from 'emailjs-com';
import { saveLead } from '../../Public/firebase';
import { buildLeadEmailHtml, buildAdvisorEmailHtml, buildConfirmationEmailHtml } from '../../Public/leadEmail';

// EmailJS template used to notify AHAVA community advisors directly.
// Leave empty ("") to disable advisor routing; set the template ID to activate it.
// Medicaid leads are NEVER routed to advisors (they are still stored + emailed to us).
// Flip to true to start emailing AHAVA's advisors (Aventura -> Advisor2@,
// Boca Raton -> advisor@, Either -> both). Medicaid leads are never forwarded.
const ADVISOR_ROUTING_ON = true;

// Meta click identifiers for server-side (Conversions API) matching. fbclid is
// kept for 7 days so a family that comes back later still attributes to the ad.
const cookie = (n) => (document.cookie.match(new RegExp('(?:^|; )' + n + '=([^;]*)')) || [])[1] || '';
function metaIds() {
    try {
        const q = new URLSearchParams(window.location.search);
        const fbclid = q.get('fbclid');
        if (fbclid) localStorage.setItem('alf_fbc', JSON.stringify({ v: 'fb.1.' + Date.now() + '.' + fbclid, t: Date.now() }));
        const stored = JSON.parse(localStorage.getItem('alf_fbc') || 'null');
        const fbc = cookie('_fbc') || (stored && Date.now() - stored.t < 7 * 864e5 ? stored.v : '');
        return { fbc, fbp: cookie('_fbp'), ua: navigator.userAgent, url: window.location.href };
    } catch (e) { return { fbc: '', fbp: '', ua: navigator.userAgent, url: window.location.href }; }
}
const newEventId = () => 'alf-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);


// All user-visible copy lives here. Facts (prices, addresses, license numbers,
// brand names, award names) stay identical across languages. Form option
// VALUES always stay English - only display labels are translated.
const T = {
    en: {
        helmet: {
            title: "Best Assisted Living in South Florida 2026 | Aventura & Boca Raton | Emetya",
            description: "Compare the top assisted living & memory care communities in South Florida for 2026. Family-owned since 2008, from $4,695/mo. Get pricing & availability today.",
            ogTitle: "Best Assisted Living in South Florida 2026 | Aventura & Boca Raton | Emetya",
            ogDescription: "Compare the top assisted living & memory care communities in South Florida. Family-owned since 2008, assisted living from $4,695/mo. Check availability & pricing.",
            keywords: "assisted living South Florida, memory care Aventura, assisted living Boca Raton, AHAVA Senior Residences, best assisted living 2026, senior living Florida"
        },
        disclosure: {
            line: "We may earn a commission from featured offers, which can affect how and where they appear. ",
            link: "Advertiser Disclosure.",
            text: "The content on this page was created independently from the Emetya editorial team. We may receive a commission from featured partners, which can influence how offers are presented - including their placement, rating, and order. In addition to compensation, we may also evaluate a brand’s reputation, popularity, and service quality when determining scores. While we strive to keep all information accurate and current, Emetya does not include every brand or offer available in the marketplace, and we cannot guarantee the completeness or accuracy of the content at the time of viewing."
        },
        headerLabel: "Assisted Living",
        proof: {
            title: "1,366 families reviewed AHAVA this month",
            text: "142 reached out to compare pricing and availability. Openings are limited - yours takes about a minute.",
        },
        menu: {
            open: "Open menu",
            close: "Close menu",
            items: [
                { id: "top", label: "Home" },
                { id: "alf-videos", label: "Videos" },
                { id: "alf-locations", label: "Locations" },
                { id: "alf-gallery", label: "Gallery" },
                { id: "alf-why", label: "Why AHAVA" },
                { id: "alf-form", label: "Pricing & Availability" },
                { id: "alf-faq", label: "FAQ" }
            ]
        },
        hero: {
            h1: "The Best Assisted Living & Memory Care in South Florida - 2026",
            sub: "Family-owned since 2008 · Aventura & Boca Raton · From $4,695/month",
            award: "U.S. News Best Memory Care 2025",
            chipLicensed: "Licensed ALF",
            chipFamily: "Family Owned Since 2008",
            ctaPrimary: "Check Availability & Pricing",
            ctaSecondary: "Schedule a Private Tour",
            lastUpdated: "Last Updated:"
        },
        ranked: {
            introTitle: "Our #1 Recommended Community",
            introSub: "We compared South Florida's assisted living and memory care communities on care quality, dining, activities, staffing and transparency. One stood above the rest.",
            badge: "Editor's Choice",
            tagline: "\"Inspired by Love, Designed for Life\"",
            attributes: [
                "Family-owned & operated since 2008 - third generation",
                "Assisted living + memory care under one roof",
                "Chef-prepared dining & daily activities",
                "24/7 care team & emergency response",
                "U.S. News & World Report Best Memory Care 2025"
            ],
            excellent: "EXCELLENT",
            ratedNo1: "Rated #1 by families in South Florida",
            promo: "Two South Florida locations - Aventura & Boca Raton - assisted living from $4,695/month",
            bookTitle: "Get pricing & availability from AHAVA",
            bookSub: "Free & no obligation - an advisor will reach out",
            bookCta: "Check Availability"
        },
        videos: {
            title: "Hear It from the People Who Live Here",
            text: "The best way to understand life at AHAVA is to hear it directly from residents and their families. From the warmth of the care team to the food, friendships, and daily activities - this is what families say about calling AHAVA home.",
            soundOn: "Tap for sound",
            captions: [
                "Nancy LaDue · AHAVA Resident",
                "Why Families Choose AHAVA",
                "A Resident's Story",
                "Inside AHAVA Aventura"
            ]
        },
        testimonials: [
            { Quote: "The level of care has been exceptional, and it has given our family enormous peace of mind.", Name: "Michelle Herzlinger", Source: "Family member" },
            { Quote: "The staff is very professional and caring. Our family has peace of mind. We are very grateful.", Name: "Barbara Toledo", Source: "Google Review" },
            { Quote: "She is always out and about with her friends, on trips or doing activities. She loves the food… and she is definitely more active and happier overall.", Name: "Tom Appleton", Source: "Google Review" },
            { Quote: "Ahava is a wonderful community. A standout feature is the on-site SoBe Rehab team, making a significant difference in residents' quality of life.", Name: "Robert Bell", Source: "Google Review" },
            { Quote: "My mother-in-law's appetite picked up because the food was so much healthier and tastier.", Name: "Heidi Feser", Source: "Family member" },
            { Quote: "My parents are well looked after, with professionalism, kindness and true, authentic care.", Name: "Andrea Vallely", Source: "Family member" }
        ],
        locations: {
            title: "Two Communities, One Standard of Love",
            sub: "Both AHAVA communities offer assisted living and memory care under one roof - so residents never need to relocate if their needs change.",
            alLabel: "Assisted Living",
            mcLabel: "Memory Care",
            from: "from",
            mo: "/mo",
            ctaPrefix: "Check Availability in ",
            licenseLabel: "ALF License",
            aventuraBullets: [
                "Assisted living & The Connections Program memory care under one roof",
                "Chef-prepared restaurant-style dining & daily activities",
                "24-hour support team & emergency response",
                "On-site physical, occupational & speech therapy (SoBe Rehab)",
                "Gardens, concierge & multilingual staff"
            ],
            bocaBullets: [
                "On-site physical, occupational & speech therapy (SoBe Rehab)",
                "Assisted living & memory care under one roof",
                "Chef-prepared dining, housekeeping & laundry",
                "Accessible apartments with walk-in showers"
            ]
        },
        gallery: {
            title: "Life at AHAVA",
            sub: "Elegant spaces designed for connection - from the atrium lounge to live music, fitness, and the bistro.",
            captions: [
                "The Atrium Lounge",
                "Bistro & Bar",
                "The Library",
                "Live Music & Events",
                "Fitness & Wellness",
                "A Team That Feels Like Family"
            ]
        },
        why: {
            title: "Why Families Choose AHAVA",
            sub: "\"Ahava\" means \"love\" in Hebrew - and it shows in every detail, from the dining room to the care team.",
            rows: [
                { Title: "Care built around the person", Text: "Personalized care plans shaped around each resident's routine and preferences - not fixed schedules." },
                { Title: "Staff who know every resident by name", Text: "A family-owned community since 2008, now in its third generation, where relationships come first." },
                { Title: "Kosher-friendly chef dining", Text: "Restaurant-style meals prepared daily by the chef, with kosher-friendly options available." },
                { Title: "A multilingual, welcoming team", Text: "Staff members who speak your family's language and make everyone feel at home." },
                { Title: "Therapy on site", Text: "Physical, occupational & speech therapy available on campus at both communities (SoBe Rehab)." },
                { Title: "No relocation if needs change", Text: "Assisted living and memory care under one roof - residents can transition between care levels without leaving their community." }
            ]
        },
        form: {
            title: "Get Pricing & Availability",
            sub: "Free, no obligation - an advisor will reach out within one business day.",
            founderRole: "Founder, AHAVA Senior Residences",
            founderAlt: "Ricki Kaneti, Founder of AHAVA Senior Residences",
            fullName: "Full Name",
            fullNamePh: "Your full name",
            phone: "Phone",
            phonePh: "(555) 555-5555",
            email: "Email",
            emailPh: "you@email.com",
            who: "Who needs care?",
            whoOptions: [
                { v: "My Mother", l: "My Mother" },
                { v: "My Father", l: "My Father" },
                { v: "My Spouse", l: "My Spouse" },
                { v: "Myself", l: "Myself" },
                { v: "Other", l: "Other" }
            ],
            location: "Preferred location",
            locationOptions: [
                { v: "Aventura", l: "Aventura" },
                { v: "Boca Raton", l: "Boca Raton" },
                { v: "Either", l: "Either" }
            ],
            careType: "Care type",
            careTypeOptions: [
                { v: "Assisted Living", l: "Assisted Living" },
                { v: "Memory Care", l: "Memory Care" },
                { v: "Not Sure Yet", l: "Not Sure Yet" }
            ],
            timeline: "Move-in timeline",
            timelineOptions: [
                { v: "As soon as possible", l: "As soon as possible" },
                { v: "1–3 months", l: "1–3 months" },
                { v: "3+ months", l: "3+ months" },
                { v: "Just researching", l: "Just researching" }
            ],
            stepOf: "Step {n} of 8",
            back: "Back",
            needsAnswer: "Please answer this question too.",
            next: "Next",
            careTypeQ: "What type of care?",
            memoryLossQ: "Does the person experience memory loss?",
            memoryLossOptions: [
                { v: "Yes", l: "Yes" },
                { v: "No", l: "No" },
                { v: "Not sure", l: "Not sure" }
            ],
            careNeedsQ: "What do they need the most help with?",
            careNeedsHint: "Select all that apply, then press Next.",
            careNeedsOptions: [
                { v: "Bathing & showering", l: "Bathing & showering" },
                { v: "Medication management", l: "Medication management" },
                { v: "Mobility assistance", l: "Mobility assistance" },
                { v: "Toileting", l: "Toileting" },
                { v: "Meals & daily supervision", l: "Meals & daily supervision" },
                { v: "None of these", l: "None of these" }
            ],
            currentLivingQ: "What is the current living situation?",
            currentLivingOptions: [
                { v: "At home", l: "At home" },
                { v: "Independent living", l: "Independent living" },
                { v: "Another assisted living community", l: "Another assisted living community" },
                { v: "Hospital or rehab", l: "Hospital or rehab" }
            ],
            budgetQ: "What monthly budget fits?",
            budgetOptions: [
                { v: "$4,500 and up", l: "$4,500 and up" },
                { v: "Under $4,500", l: "Under $4,500" },
                { v: "Not sure yet", l: "Not sure yet" }
            ],
            paymentQ: "How would care be paid?",
            paymentOptions: [
                { v: "Private funds (income & savings)", l: "Private funds (income & savings)" },
                { v: "Long-term care insurance", l: "Long-term care insurance" },
                { v: "VA benefits", l: "VA benefits" },
                { v: "Medicaid", l: "Medicaid" },
                { v: "Not sure", l: "Not sure" }
            ],
            contactQ: "Where should we send your pricing?",
            contactHint: "An advisor will reach out within one business day.",
            recipientName: "Care recipient's first name (optional)",
            recipientNamePh: "First name",
            recipientAge: "Age (optional)",
            recipientAgePh: "Age",
            submit: "Get My Pricing & Availability",
            sending: "Sending…",
            error: "Something went wrong sending your request. Please try again in a moment.",
            consent: "By submitting, you agree to be contacted about senior living options and consent to your information being shared with matching senior living communities. No spam, ever.",
            thanksTitle: "Thank you!",
            thanksText: "An advisor will call you shortly to schedule your private tour."
        },
        faq: {
            title: "Frequently Asked Questions",
            items: [
                {
                    Question: "How much does assisted living at AHAVA cost, and what's included?",
                    Answer: "Assisted living starts at $4,695/month in Boca Raton and $5,750/month in Aventura; memory care starts at $5,795/month in Boca Raton and $6,900/month in Aventura. Monthly rates include chef-prepared restaurant-style dining, daily activities, housekeeping and laundry, medication management, concierge service, and a 24-hour support team with emergency response. Submit the form above for exact pricing based on your family's needs."
                },
                {
                    Question: "What's the difference between assisted living and memory care?",
                    Answer: "Assisted living offers help with daily activities - like bathing, dressing, and medication management - while residents enjoy an active, independent lifestyle. Memory care (AHAVA's Connections Program) provides a secure, structured environment with specialized programming for residents living with Alzheimer's or other forms of dementia. Because AHAVA offers both under one roof, residents can move between care levels without relocating to a new community."
                },
                {
                    Question: "Is short-term or respite care available?",
                    Answer: "Yes - short-term respite stays are available at both communities, subject to availability. Respite care is a great option after a hospital stay, while a family caregiver travels, or as a trial stay before making a long-term decision. An advisor can confirm current availability for your dates."
                },
                {
                    Question: "How do I schedule a tour?",
                    Answer: "Simply fill out the form on this page - it takes less than a minute. An advisor will reach out within one business day to answer your questions and arrange a private tour at whichever community is most convenient for your family."
                },
                {
                    Question: "What areas does AHAVA serve?",
                    Answer: "AHAVA Senior Residences serves families across South Florida, with communities in Aventura (Miami-Dade County) and Boca Raton (Palm Beach County). Families from Miami-Dade, Broward, and Palm Beach counties - including Miami, Fort Lauderdale, Hollywood, Sunny Isles, Delray Beach, and Boynton Beach - regularly choose AHAVA for its location, care quality, and family-owned approach."
                }
            ]
        },
        footer: {
            p1: "AHAVA Senior Residences is a featured partner. Emetya may receive compensation when you submit an inquiry. Emetya is an independent comparison service, not a healthcare provider; information is for general purposes and is not medical advice.",
            p2: "Information provided on Emetya is for educational purposes only and is not intended as medical advice. Your health and wellness needs are personal, and the products or services featured may not be suitable for your individual situation. Emetya does not provide medical diagnoses, treatment plans, or personalized health recommendations. Always consult a licensed healthcare provider for advice tailored to your specific medical needs.",
            rights: "©2026 Emetya Health. All rights reserved.",
            privacy: "Privacy Policy",
            terms: "Terms of Use"
        },
        sticky: "Get Pricing & Availability"
    },
    es: {
        helmet: {
            title: "La Mejor Residencia Asistida en el Sur de la Florida 2026 | Aventura y Boca Raton | Emetya",
            description: "Compare las mejores comunidades de residencia asistida y cuidado de la memoria en el Sur de la Florida para 2026. Empresa familiar desde 2008, desde $4,695/mes. Reciba precios y disponibilidad hoy.",
            ogTitle: "La Mejor Residencia Asistida en el Sur de la Florida 2026 | Aventura y Boca Raton | Emetya",
            ogDescription: "Compare las mejores comunidades de residencia asistida y cuidado de la memoria en el Sur de la Florida. Empresa familiar desde 2008, residencia asistida desde $4,695/mes. Consulte disponibilidad y precios.",
            keywords: "residencia asistida Sur de la Florida, cuidado de la memoria Aventura, residencia asistida Boca Raton, AHAVA Senior Residences, mejor residencia asistida 2026, residencias para adultos mayores Florida"
        },
        disclosure: {
            line: "Podemos recibir una comisión por las ofertas destacadas, lo cual puede influir en cómo y dónde aparecen. ",
            link: "Divulgación del Anunciante.",
            text: "El contenido de esta página fue creado de manera independiente por el equipo editorial de Emetya. Podemos recibir una comisión de los socios destacados, lo cual puede influir en cómo se presentan las ofertas - incluyendo su ubicación, calificación y orden. Además de la compensación, también podemos evaluar la reputación, popularidad y calidad de servicio de una marca al determinar las puntuaciones. Si bien nos esforzamos por mantener toda la información precisa y actualizada, Emetya no incluye todas las marcas u ofertas disponibles en el mercado, y no podemos garantizar la integridad o exactitud del contenido al momento de su visualización."
        },
        headerLabel: "Residencia Asistida",
        proof: {
            title: "1,366 familias vieron AHAVA este mes",
            text: "142 solicitaron precios y disponibilidad. Los cupos son limitados - el suyo toma un minuto.",
        },
        menu: {
            open: "Abrir menú",
            close: "Cerrar menú",
            items: [
                { id: "top", label: "Inicio" },
                { id: "alf-videos", label: "Videos" },
                { id: "alf-locations", label: "Ubicaciones" },
                { id: "alf-gallery", label: "Galería" },
                { id: "alf-why", label: "Por Qué AHAVA" },
                { id: "alf-form", label: "Precios y Disponibilidad" },
                { id: "alf-faq", label: "Preguntas Frecuentes" }
            ]
        },
        hero: {
            h1: "La Mejor Residencia Asistida y Cuidado de la Memoria en el Sur de la Florida - 2026",
            sub: "Empresa familiar desde 2008 · Aventura y Boca Raton · Desde $4,695/mes",
            award: "U.S. News Best Memory Care 2025",
            chipLicensed: "ALF con Licencia",
            chipFamily: "Empresa Familiar desde 2008",
            ctaPrimary: "Ver Precios y Disponibilidad",
            ctaSecondary: "Agende una Visita Privada",
            lastUpdated: "Última Actualización:"
        },
        ranked: {
            introTitle: "Nuestra Comunidad #1 Recomendada",
            introSub: "Comparamos las comunidades de residencia asistida y cuidado de memoria del sur de la Florida segun la calidad del cuidado, la comida, las actividades, el personal y la transparencia. Una destaco por encima de todas.",
            badge: "Selección del Editor",
            tagline: "\"Inspirada en el Amor, Diseñada para la Vida\"",
            attributes: [
                "Empresa familiar desde 2008 - tercera generación",
                "Residencia asistida + cuidado de la memoria bajo un mismo techo",
                "Comidas preparadas por chef y actividades diarias",
                "Equipo de cuidado 24/7 y respuesta de emergencia",
                "U.S. News & World Report Best Memory Care 2025"
            ],
            excellent: "EXCELENTE",
            ratedNo1: "Calificada #1 por familias del Sur de la Florida",
            promo: "Dos ubicaciones en el Sur de la Florida - Aventura y Boca Raton - residencia asistida desde $4,695/mes",
            bookTitle: "Reciba precios y disponibilidad de AHAVA",
            bookSub: "Gratis y sin compromiso - un asesor le contactará",
            bookCta: "Ver Disponibilidad"
        },
        videos: {
            title: "Escúchelo de Quienes Viven Aquí",
            text: "La mejor manera de entender la vida en AHAVA es escucharlo directamente de los residentes y sus familias. Desde la calidez del equipo de cuidado hasta la comida, las amistades y las actividades diarias - esto es lo que las familias dicen sobre llamar hogar a AHAVA.",
            soundOn: "Toque para escuchar",
            captions: [
                "Nancy LaDue · Residente de AHAVA",
                "Por Qué las Familias Eligen AHAVA",
                "La Historia de una Residente",
                "Conozca AHAVA Aventura"
            ]
        },
        testimonials: [
            { Quote: "El nivel de cuidado ha sido excepcional, y le ha dado a nuestra familia una enorme tranquilidad.", Name: "Michelle Herzlinger", Source: "Familiar" },
            { Quote: "El personal es muy profesional y atento. Nuestra familia tiene tranquilidad. Estamos muy agradecidos.", Name: "Barbara Toledo", Source: "Reseña de Google" },
            { Quote: "Siempre está de un lado a otro con sus amigas, en paseos o haciendo actividades. Le encanta la comida… y definitivamente está más activa y más feliz en general.", Name: "Tom Appleton", Source: "Reseña de Google" },
            { Quote: "Ahava es una comunidad maravillosa. Algo que se destaca es el equipo de SoBe Rehab en el propio centro, que marca una diferencia significativa en la calidad de vida de los residentes.", Name: "Robert Bell", Source: "Reseña de Google" },
            { Quote: "El apetito de mi suegra mejoró porque la comida era mucho más saludable y sabrosa.", Name: "Heidi Feser", Source: "Familiar" },
            { Quote: "Mis padres están bien atendidos, con profesionalismo, amabilidad y un cuidado verdadero y auténtico.", Name: "Andrea Vallely", Source: "Familiar" }
        ],
        locations: {
            title: "Dos Comunidades, Un Mismo Estándar de Amor",
            sub: "Ambas comunidades AHAVA ofrecen residencia asistida y cuidado de la memoria bajo un mismo techo - así los residentes nunca necesitan mudarse si sus necesidades cambian.",
            alLabel: "Residencia Asistida",
            mcLabel: "Cuidado de la Memoria",
            from: "desde",
            mo: "/mes",
            ctaPrefix: "Ver Disponibilidad en ",
            licenseLabel: "Licencia ALF",
            aventuraBullets: [
                "Residencia asistida y cuidado de la memoria The Connections Program bajo un mismo techo",
                "Comidas estilo restaurante preparadas por chef y actividades diarias",
                "Equipo de apoyo las 24 horas y respuesta de emergencia",
                "Terapia física, ocupacional y del habla en el propio centro (SoBe Rehab)",
                "Jardines, conserjería y personal multilingüe"
            ],
            bocaBullets: [
                "Terapia física, ocupacional y del habla en el propio centro (SoBe Rehab)",
                "Residencia asistida y cuidado de la memoria bajo un mismo techo",
                "Comidas preparadas por chef, limpieza y lavandería",
                "Apartamentos accesibles con duchas sin escalón"
            ]
        },
        gallery: {
            title: "La Vida en AHAVA",
            sub: "Espacios elegantes diseñados para la conexión - desde el salón del atrio hasta la música en vivo, el ejercicio y el bistró.",
            captions: [
                "El Salón del Atrio",
                "Bistró y Bar",
                "La Biblioteca",
                "Música en Vivo y Eventos",
                "Ejercicio y Bienestar",
                "Un Equipo que se Siente como Familia"
            ]
        },
        why: {
            title: "Por Qué las Familias Eligen AHAVA",
            sub: "\"Ahava\" significa \"amor\" en hebreo - y se nota en cada detalle, desde el comedor hasta el equipo de cuidado.",
            rows: [
                { Title: "Cuidado centrado en la persona", Text: "Planes de cuidado personalizados según la rutina y las preferencias de cada residente - no horarios fijos." },
                { Title: "Un personal que conoce a cada residente por su nombre", Text: "Una comunidad familiar desde 2008, ahora en su tercera generación, donde las relaciones son lo primero." },
                { Title: "Cocina de chef con opciones kosher", Text: "Comidas estilo restaurante preparadas a diario por el chef, con opciones aptas para kosher disponibles." },
                { Title: "Un equipo multilingüe y acogedor", Text: "Personal que habla el idioma de su familia y hace que todos se sientan como en casa." },
                { Title: "Terapia en el propio centro", Text: "Terapia física, ocupacional y del habla disponible en ambas comunidades (SoBe Rehab)." },
                { Title: "Sin mudanzas si las necesidades cambian", Text: "Residencia asistida y cuidado de la memoria bajo un mismo techo - los residentes pueden pasar de un nivel de cuidado a otro sin dejar su comunidad." }
            ]
        },
        form: {
            title: "Reciba Precios y Disponibilidad",
            sub: "Gratis y sin compromiso - un asesor le contactará dentro de un día hábil.",
            founderRole: "Fundadora, AHAVA Senior Residences",
            founderAlt: "Ricki Kaneti, Fundadora de AHAVA Senior Residences",
            fullName: "Nombre Completo",
            fullNamePh: "Su nombre completo",
            phone: "Teléfono",
            phonePh: "(555) 555-5555",
            email: "Correo Electrónico",
            emailPh: "usted@correo.com",
            who: "¿Quién necesita cuidado?",
            whoOptions: [
                { v: "My Mother", l: "Mi madre" },
                { v: "My Father", l: "Mi padre" },
                { v: "My Spouse", l: "Mi cónyuge" },
                { v: "Myself", l: "Yo mismo(a)" },
                { v: "Other", l: "Otro" }
            ],
            location: "Ubicación preferida",
            locationOptions: [
                { v: "Aventura", l: "Aventura" },
                { v: "Boca Raton", l: "Boca Raton" },
                { v: "Either", l: "Cualquiera de las dos" }
            ],
            careType: "Tipo de cuidado",
            careTypeOptions: [
                { v: "Assisted Living", l: "Residencia Asistida" },
                { v: "Memory Care", l: "Cuidado de la Memoria" },
                { v: "Not Sure Yet", l: "Aún no estoy seguro(a)" }
            ],
            timeline: "Plazo de mudanza",
            timelineOptions: [
                { v: "As soon as possible", l: "Lo antes posible" },
                { v: "1–3 months", l: "1–3 meses" },
                { v: "3+ months", l: "Más de 3 meses" },
                { v: "Just researching", l: "Solo estoy investigando" }
            ],
            stepOf: "Paso {n} de 8",
            back: "Atrás",
            needsAnswer: "Por favor responda también esta pregunta.",
            next: "Siguiente",
            careTypeQ: "¿Qué tipo de cuidado?",
            memoryLossQ: "¿La persona experimenta pérdida de memoria?",
            memoryLossOptions: [
                { v: "Yes", l: "Sí" },
                { v: "No", l: "No" },
                { v: "Not sure", l: "No estoy seguro(a)" }
            ],
            careNeedsQ: "¿Con qué necesita más ayuda?",
            careNeedsHint: "Seleccione todas las que apliquen y presione Siguiente.",
            careNeedsOptions: [
                { v: "Bathing & showering", l: "Baño y ducha" },
                { v: "Medication management", l: "Manejo de medicamentos" },
                { v: "Mobility assistance", l: "Asistencia para moverse" },
                { v: "Toileting", l: "Uso del baño" },
                { v: "Meals & daily supervision", l: "Comidas y supervisión diaria" },
                { v: "None of these", l: "Ninguna de estas" }
            ],
            currentLivingQ: "¿Cuál es la situación de vivienda actual?",
            currentLivingOptions: [
                { v: "At home", l: "En casa" },
                { v: "Independent living", l: "Vida independiente" },
                { v: "Another assisted living community", l: "Otra comunidad de residencia asistida" },
                { v: "Hospital or rehab", l: "Hospital o rehabilitación" }
            ],
            budgetQ: "¿Qué presupuesto mensual se ajusta?",
            budgetOptions: [
                { v: "$4,500 and up", l: "$4,500 o más" },
                { v: "Under $4,500", l: "Menos de $4,500" },
                { v: "Not sure yet", l: "Aún no estoy seguro(a)" }
            ],
            paymentQ: "¿Cómo se pagaría el cuidado?",
            paymentOptions: [
                { v: "Private funds (income & savings)", l: "Fondos privados (ingresos y ahorros)" },
                { v: "Long-term care insurance", l: "Seguro de cuidado a largo plazo" },
                { v: "VA benefits", l: "Beneficios de veteranos (VA)" },
                { v: "Medicaid", l: "Medicaid" },
                { v: "Not sure", l: "No estoy seguro(a)" }
            ],
            contactQ: "¿A dónde enviamos sus precios?",
            contactHint: "Un asesor le contactará dentro de un día hábil.",
            recipientName: "Nombre de quien recibirá el cuidado (opcional)",
            recipientNamePh: "Nombre",
            recipientAge: "Edad (opcional)",
            recipientAgePh: "Edad",
            submit: "Recibir Precios y Disponibilidad",
            sending: "Enviando…",
            error: "Ocurrió un error al enviar su solicitud. Por favor, inténtelo de nuevo en un momento.",
            consent: "Al enviar, usted acepta ser contactado sobre opciones de residencias para adultos mayores y autoriza que su información sea compartida con comunidades de residencias que se ajusten a sus necesidades. Nunca enviamos spam.",
            thanksTitle: "¡Gracias!",
            thanksText: "Un asesor le llamará en breve para agendar su visita privada."
        },
        faq: {
            title: "Preguntas Frecuentes",
            items: [
                {
                    Question: "¿Cuánto cuesta la residencia asistida en AHAVA y qué incluye?",
                    Answer: "La residencia asistida comienza en $4,695/mes en Boca Raton y $5,750/mes en Aventura; el cuidado de la memoria comienza en $5,795/mes en Boca Raton y $6,900/mes en Aventura. Las tarifas mensuales incluyen comidas estilo restaurante preparadas por chef, actividades diarias, limpieza y lavandería, manejo de medicamentos, servicio de conserjería y un equipo de apoyo las 24 horas con respuesta de emergencia. Envíe el formulario de arriba para recibir precios exactos según las necesidades de su familia."
                },
                {
                    Question: "¿Cuál es la diferencia entre residencia asistida y cuidado de la memoria?",
                    Answer: "La residencia asistida ofrece ayuda con las actividades diarias - como bañarse, vestirse y el manejo de medicamentos - mientras los residentes disfrutan de un estilo de vida activo e independiente. El cuidado de la memoria (The Connections Program de AHAVA) brinda un entorno seguro y estructurado con programación especializada para residentes que viven con Alzheimer u otras formas de demencia. Como AHAVA ofrece ambos bajo un mismo techo, los residentes pueden pasar de un nivel de cuidado a otro sin mudarse a una nueva comunidad."
                },
                {
                    Question: "¿Hay cuidado de corta estancia o de relevo disponible?",
                    Answer: "Sí - hay estancias cortas de relevo disponibles en ambas comunidades, sujetas a disponibilidad. El cuidado de relevo es una excelente opción después de una hospitalización, mientras el cuidador familiar viaja, o como una estancia de prueba antes de tomar una decisión a largo plazo. Un asesor puede confirmar la disponibilidad actual para sus fechas."
                },
                {
                    Question: "¿Cómo agendo una visita?",
                    Answer: "Simplemente complete el formulario en esta página - toma menos de un minuto. Un asesor le contactará dentro de un día hábil para responder sus preguntas y organizar una visita privada en la comunidad que sea más conveniente para su familia."
                },
                {
                    Question: "¿Qué áreas atiende AHAVA?",
                    Answer: "AHAVA Senior Residences atiende a familias en todo el Sur de la Florida, con comunidades en Aventura (condado de Miami-Dade) y Boca Raton (condado de Palm Beach). Familias de los condados de Miami-Dade, Broward y Palm Beach - incluyendo Miami, Fort Lauderdale, Hollywood, Sunny Isles, Delray Beach y Boynton Beach - eligen regularmente a AHAVA por su ubicación, la calidad de su cuidado y su enfoque de empresa familiar."
                }
            ]
        },
        footer: {
            p1: "AHAVA Senior Residences es un socio destacado. Emetya puede recibir compensación cuando usted envía una consulta. Emetya es un servicio de comparación independiente, no un proveedor de atención médica; la información es para fines generales y no constituye consejo médico.",
            p2: "La información proporcionada en Emetya es solo para fines educativos y no pretende ser consejo médico. Sus necesidades de salud y bienestar son personales, y los productos o servicios presentados pueden no ser adecuados para su situación individual. Emetya no ofrece diagnósticos médicos, planes de tratamiento ni recomendaciones de salud personalizadas. Siempre consulte a un proveedor de salud con licencia para recibir orientación adaptada a sus necesidades médicas específicas.",
            rights: "©2026 Emetya Health. Todos los derechos reservados.",
            privacy: "Política de Privacidad",
            terms: "Términos de Uso"
        },
        sticky: "Recibir Precios y Disponibilidad"
    }
};

export default function Alf() {
    const [Disclosure, setDisclosure] = useState(false);
    const [Lang, setLang] = useState(new URLSearchParams(window.location.search).get('lang') === 'es' ? 'es' : 'en');
    const [MenuOpen, setMenuOpen] = useState(false);
    const [WizardStep, setWizardStep] = useState(1);
    const [WhoNeedsCare, setWhoNeedsCare] = useState('');
    const [PreferredLocation, setPreferredLocation] = useState('');
    const [CareType, setCareType] = useState('');
    const [MemoryLoss, setMemoryLoss] = useState('');
    const [CareNeeds, setCareNeeds] = useState([]);
    const [CurrentLiving, setCurrentLiving] = useState('');
    const [Timeline, setTimeline] = useState('');
    const [Budget, setBudget] = useState('');
    const [PaymentSource, setPaymentSource] = useState('');
    const [Sending, setSending] = useState(false);
    const [Submitted, setSubmitted] = useState(false);
    const [WizardOpen, setWizardOpen] = useState(false);
    // Set when the user steps backward: revisited two-question screens then wait
    // for an explicit Next instead of jumping forward on the first tap.
    const [WentBack, setWentBack] = useState(false);
    const [Proof, setProof] = useState(false);
    const [ProofClosed, setProofClosed] = useState(false);

    // Social-proof nudge: appears once the visitor has scrolled past the hero or
    // after a short dwell, then retires itself so it never nags.
    React.useEffect(() => {
        if (ProofClosed) return;
        let shown = false;
        const show = () => {
            if (shown || ProofClosed) return;
            shown = true;
            setProof(true);
            window.setTimeout(() => setProof(false), 11000);
        };
        const onScroll = () => { if (window.scrollY > 700) show(); };
        const timer = window.setTimeout(show, 12000);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => { window.clearTimeout(timer); window.removeEventListener('scroll', onScroll); };
    }, [ProofClosed]);
    const [SendError, setSendError] = useState(false);
    const [OpenFaq, setOpenFaq] = useState(null);
    const [Geo, setGeo] = useState({ city: '', state: '', zip: '', ip: '' });
    const VideoTrack = React.useRef(null);
    const VideoSection = React.useRef(null);
    // Browsers only allow audio after a real user gesture, so clips start muted
    // and switch to sound on the visitor's first tap anywhere on the page.
    const [SoundOn, setSoundOn] = useState(false);
    const SoundRef = React.useRef(false);

    React.useEffect(() => {
        const enable = () => {
            SoundRef.current = true;
            setSoundOn(true);
            const track = VideoTrack.current;
            if (track) track.querySelectorAll('video').forEach((v) => { v.muted = false; v.volume = 1; });
        };
        document.addEventListener('pointerdown', enable, { once: true });
        document.addEventListener('keydown', enable, { once: true });
        return () => {
            document.removeEventListener('pointerdown', enable);
            document.removeEventListener('keydown', enable);
        };
    }, []);

    // The testimonial row plays itself: the first clip starts (muted, so browsers
    // allow it) once the section scrolls into view, each clip hands off to the
    // next when it ends, and everything pauses once the section leaves the screen.
    React.useEffect(() => {
        const track = VideoTrack.current;
        const section = VideoSection.current;
        if (!track || !section) return;
        const videos = Array.from(track.querySelectorAll('video'));
        if (!videos.length) return;
        let current = 0;
        let inView = false;

        const pauseAll = () => videos.forEach((v) => { if (!v.paused) v.pause(); });
        const play = (i) => {
            if (!inView || i >= videos.length) return;
            current = i;
            videos.forEach((v, n) => { if (n !== i && !v.paused) v.pause(); });
            const v = videos[i];
            v.muted = !SoundRef.current;
            v.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            const p = v.play();
            if (p && p.catch) p.catch(() => { });
        };

        const onEnded = (i) => () => play(i + 1);
        const onPlay = (i) => () => { current = i; };
        const endedHandlers = videos.map((v, i) => { const h = onEnded(i); v.addEventListener('ended', h); return h; });
        const playHandlers = videos.map((v, i) => { const h = onPlay(i); v.addEventListener('play', h); return h; });

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                inView = e.isIntersecting && e.intersectionRatio >= 0.35;
                if (inView) {
                    if (videos.every((v) => v.paused)) play(current);
                } else {
                    pauseAll();
                }
            });
        }, { threshold: [0, 0.35, 0.6] });
        observer.observe(section);

        return () => {
            observer.disconnect();
            videos.forEach((v, i) => {
                v.removeEventListener('ended', endedHandlers[i]);
                v.removeEventListener('play', playHandlers[i]);
            });
        };
    }, [Lang]);

    // Video row scrolls horizontally; arrows nudge it by roughly one card.
    const scrollVideos = (dir) => {
        const el = VideoTrack.current;
        if (!el) return;
        const card = el.querySelector('.AlfVideoPhone');
        const step = card ? card.offsetWidth + 30 : el.clientWidth * 0.8;
        el.scrollBy({ left: dir * step, behavior: 'smooth' });
    };

    const t = T[Lang];
    const LastUpdated = new Date().toLocaleDateString(Lang === 'es' ? 'es-US' : 'en-US', { year: 'numeric', month: 'long' });

    // Capture visitor location (city/state/zip) once on mount, so submits are instant.
    React.useEffect(() => {
        (async () => {
            try {
                const res = await fetch('https://ipapi.co/json/');
                const d = await res.json();
                if (d && d.city) {
                    setGeo({ city: d.city || '', state: d.region || '', zip: d.postal || '', ip: d.ip || '' });
                    return;
                }
                throw new Error('empty');
            } catch (e) {
                try {
                    const res2 = await fetch('https://ipwho.is/');
                    const d2 = await res2.json();
                    if (d2 && d2.success !== false) {
                        setGeo({ city: d2.city || '', state: d2.region || '', zip: d2.postal || '', ip: d2.ip || '' });
                    }
                } catch (e2) { }
            }
        })();
    }, []);

    // Lock body scroll while the mobile menu is open; restore after close.
    React.useEffect(() => {
        document.body.style.overflow = MenuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [MenuOpen]);

    // Every CTA opens the quiz as a focused full-screen layer, the way A Place
    // for Mom hands its quiz its own page. Body scroll is locked while it is up.
    const scrollToForm = (location) => {
        if (location) setPreferredLocation(location);
        setWizardOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeWizard = () => {
        setWizardOpen(false);
        document.body.style.overflow = '';
    };

    const menuNavigate = (id) => {
        setMenuOpen(false);
        document.body.style.overflow = '';
        window.setTimeout(() => {
            if (id === 'top') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 60);
    };

    // Wizard navigation - auto-advance shortly after a tap so the selected state
    // stays visible; two-question screens advance once both answers are in.
    const goToStep = (next) => {
        window.setTimeout(() => setWizardStep(next), 220);
    };
    const pickWho = (v) => { setWhoNeedsCare(v); goToStep(2); };
    const pickCareType = (v) => { setCareType(v); if (MemoryLoss) goToStep(3); };
    const pickMemoryLoss = (v) => { setMemoryLoss(v); if (CareType) goToStep(3); };
    const pickCareNeed = (v) => { setCareNeeds([v]); goToStep(4); };

    const pickCurrentLiving = (v) => { setCurrentLiving(v); goToStep(5); };
    const pickTimeline = (v) => { setTimeline(v); goToStep(6); };
    const pickBudget = (v) => { setBudget(v); if (PaymentSource) goToStep(7); };
    const pickPaymentSource = (v) => { setPaymentSource(v); if (Budget) goToStep(7); };
    const pickLocation = (v) => { setPreferredLocation(v); goToStep(8); };


    // Derived lead fields - submitted in English, like every form value.
    const CareNeedsString = CareNeeds.join(', ');
    const ContactRelationship = WhoNeedsCare === 'My Mother' || WhoNeedsCare === 'My Father' ? 'Child of resident'
        : WhoNeedsCare === 'My Spouse' ? 'Spouse'
            : WhoNeedsCare === 'Myself' ? 'Self'
                : 'Other';
    const AdvisorEmail = PreferredLocation === 'Aventura' ? 'Advisor2@colonialalf.com'
        : PreferredLocation === 'Boca Raton' ? 'advisor@colonialalf.com'
            : 'advisor@colonialalf.com, Advisor2@colonialalf.com';
    const MedicaidFlag = PaymentSource === 'Medicaid' ? 'YES' : 'NO';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (Sending) return;
        setSending(true);
        setSendError(false);
        const form = e.target;
        const RecipientName = form.Recipient_Name.value;
        const RecipientAge = form.Recipient_Age.value;
        const EventId = newEventId();
        const MetaIds = metaIds();
        const lead = {
            fullName: form.Full_Name.value,
            phone: form.Phone.value,
            email: form.Email.value,
            whoNeedsCare: WhoNeedsCare,
            preferredLocation: PreferredLocation,
            careType: CareType,
            timeline: Timeline,
            budget: Budget,
            paymentSource: PaymentSource,
            careNeeds: CareNeedsString,
            currentLiving: CurrentLiving,
            memoryLoss: MemoryLoss,
            recipientName: RecipientName,
            recipientAge: RecipientAge,
            contactRelationship: ContactRelationship,
            advisorEmail: AdvisorEmail,
            medicaid: PaymentSource === 'Medicaid',
            city: Geo.city,
            state: Geo.state,
            zip: Geo.zip,
            ip: Geo.ip,
            partner: "AHAVA Senior Residences",
        };
        // Lead goes to the Genaya CRM (via relay), Firestore, and email - success if any lands.
        const submissionId = `alf_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        // No single channel may stall the submit: Firestore in particular retries
        // silently when offline, which would otherwise hang the whole flow and
        // leave the visitor staring at a spinner.
        const withTimeout = (promise, ms, fallback) => Promise.race([
            Promise.resolve(promise).catch(() => fallback),
            new Promise((resolve) => window.setTimeout(() => resolve(fallback), ms)),
        ]);
        const [emailOk, storedId, crmOk] = await Promise.all([
            emailjs.send('service_fqud6um', 'template_an122h9', {
                to_email: 'genesisplatformsinfo@gmail.com',
                subject: `New ALF Lead - ${lead.fullName} (${PreferredLocation})`,
                message_html: buildLeadEmailHtml({ ...lead, timeline: Timeline, source: 'emetya.com/alf' + window.location.search, submittedAt: new Date().toISOString() }),
                // Legacy field names too, so an older template still renders real
                // values instead of blanks if it is ever the active one.
                Full_Name: lead.fullName,
                Phone: lead.phone,
                Email: lead.email,
                Who_Needs_Care: WhoNeedsCare,
                Contact_Relationship: ContactRelationship,
                Preferred_Location: PreferredLocation,
                Care_Type: CareType,
                Memory_Loss: MemoryLoss,
                Care_Needs: CareNeedsString,
                Current_Living: CurrentLiving,
                Timeline: Timeline,
                Budget: Budget,
                Payment_Source: PaymentSource,
                Medicaid_Flag: MedicaidFlag,
                Recipient_Name: RecipientName,
                Recipient_Age: RecipientAge,
                Advisor_Email: AdvisorEmail,
                Lead_City: Geo.city,
                Lead_State: Geo.state,
                Lead_Zip: Geo.zip,
                Lead_Source: 'emetya.com/alf' + window.location.search,
                Message: ComposedMessage,
            }, 'QeqW-bXHRNjRmhZ6I').then(() => true).catch(() => false),
            withTimeout(saveLead('alfLeads', lead), 8000, null),
            fetch(window.location.hostname === 'localhost' ? 'https://us-central1-ahava-alf.cloudfunctions.net/submitAlfLead' : '/api/alf-lead', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    submissionId: submissionId,
                    fullName: lead.fullName,
                    phone: lead.phone,
                    email: lead.email,
                    whoNeedsCare: WhoNeedsCare,
                    preferredLocation: PreferredLocation,
                    careType: CareType,
                    timeline: Timeline,
                    budget: Budget,
                    paymentSource: PaymentSource,
                    careNeeds: CareNeedsString,
                    currentLiving: CurrentLiving,
                    memoryLoss: MemoryLoss,
                    recipientName: RecipientName,
                    recipientAge: RecipientAge,
                    contactRelationship: ContactRelationship,
                    advisorEmail: AdvisorEmail,
                    medicaid: PaymentSource === 'Medicaid',
                    city: Geo.city,
                    state: Geo.state,
                    zip: Geo.zip,
                    source: 'emetya.com/alf' + window.location.search,
                    notes: ComposedMessage,
                    fb: { eventId: EventId, fbc: MetaIds.fbc, fbp: MetaIds.fbp, ua: MetaIds.ua, url: MetaIds.url },
                }),
            }).then((r) => r.ok).catch(() => false),
        ].map((p, i) => (i === 1 ? p : withTimeout(p, 12000, false))));
        // Confirmation to the family, so they know a person is coming back to them.
        if (lead.email) {
            emailjs.send('service_fqud6um', 'template_an122h9', {
                to_email: lead.email,
                subject: 'We received your request - AHAVA Senior Residences',
                message_html: buildConfirmationEmailHtml({ ...lead, timeline: Timeline }),
            }, 'QeqW-bXHRNjRmhZ6I').catch(() => { });
        }

        // Advisor routing - fire-and-forget copy to the matching community advisor(s).
        // Never fired for Medicaid leads, and a failure here never affects the thank-you state.
        if (ADVISOR_ROUTING_ON && PaymentSource !== 'Medicaid') {
            const advisorHtml = buildAdvisorEmailHtml({ ...lead, timeline: Timeline, source: 'emetya.com/alf' + window.location.search, submittedAt: new Date().toISOString() });
            const targets = PreferredLocation === 'Aventura' ? ['Advisor2@colonialalf.com']
                : PreferredLocation === 'Boca Raton' ? ['advisor@colonialalf.com']
                    : ['Advisor2@colonialalf.com', 'advisor@colonialalf.com'];
            targets.forEach((addr) => {
                emailjs.send('service_fqud6um', 'template_an122h9', {
                    to_email: addr,
                    subject: `New Referral for AHAVA ${PreferredLocation} - ${lead.fullName}`,
                    message_html: advisorHtml,
                }, 'QeqW-bXHRNjRmhZ6I').catch(() => { });
            });
        }
        if (emailOk || storedId || crmOk) {
            setSubmitted(true);
            // Three audiences for Meta: real buyers fire the standard Lead event
            // (what the ad sets optimize for); Medicaid and not-ready submissions
            // fire custom events that feed exclusion audiences.
            if (window.fbq) {
                const lowBudget = Budget === 'Under $4,500';
                const tier = PaymentSource === 'Medicaid' ? 'MedicaidLead'
                    : (lowBudget || Timeline === 'Just researching') ? 'NotReadyLead'
                        : 'Lead';
                const params = { content_name: 'AHAVA ALF', budget: Budget, payment: PaymentSource, timeline: Timeline, location: PreferredLocation };
                if (tier === 'Lead') window.fbq('track', 'Lead', params, { eventID: EventId }); else window.fbq('trackCustom', tier, params, { eventID: EventId });
            }
        } else {
            setSendError(true);
        }
        setSending(false);
    };

    const ComposedMessage = `ALF Lead - Who: ${WhoNeedsCare} (${ContactRelationship}) | Location: ${PreferredLocation} | Care: ${CareType} | Memory Loss: ${MemoryLoss} | Needs: ${CareNeedsString} | Living: ${CurrentLiving} | Timeline: ${Timeline} | Budget: ${Budget} | Payment: ${PaymentSource} (Medicaid: ${MedicaidFlag}) | Advisor: ${AdvisorEmail} | From: ${Geo.city}${Geo.state ? ', ' + Geo.state : ''} ${Geo.zip} | Source: emetya.com/alf${window.location.search}`;

    const Locations = [
        {
            Name: "AHAVA Aventura",
            Img: require(`../../AlfImages/ahava-exterior.jpg`),
            Address: "2777 NE 183rd St, Aventura, FL 33160",
            AlPrice: "$5,750",
            McPrice: "$6,900",
            License: `${t.locations.licenseLabel} #10117`,
            FormValue: "Aventura",
            Bullets: t.locations.aventuraBullets
        },
        {
            Name: "AHAVA Boca Raton",
            Img: require(`../../AlfImages/ahava-loc-boca-front.jpg`),
            Address: "1080 NW 15th St, Boca Raton, FL 33486",
            AlPrice: "$4,695",
            McPrice: "$5,795",
            License: `${t.locations.licenseLabel} #7352`,
            FormValue: "Boca Raton",
            Bullets: t.locations.bocaBullets
        }
    ];

    const GalleryImages = [
        { Img: require(`../../AlfImages/ahava-g-atrium-lounge.jpg`), Caption: t.gallery.captions[0] },
        { Img: require(`../../AlfImages/ahava-g-bistro-bar.jpg`), Caption: t.gallery.captions[1] },
        { Img: require(`../../AlfImages/ahava-g-library.jpg`), Caption: t.gallery.captions[2] },
        { Img: require(`../../AlfImages/ahava-cal-music.jpg`), Caption: t.gallery.captions[3] },
        { Img: require(`../../AlfImages/ahava-cal-exercise.jpg`), Caption: t.gallery.captions[4] },
        { Img: require(`../../AlfImages/ahava-closing.jpg`), Caption: t.gallery.captions[5] }
    ];

    const WhyRows = t.why.rows;
    const Testimonials = t.testimonials;
    const Faqs = t.faq.items;

    const LangToggleEl = (
        <div className="AlfLangToggle">
            <button type="button" className={Lang === 'en' ? "AlfLangActive" : ""} onClick={() => setLang('en')}>EN</button>
            <button type="button" className={Lang === 'es' ? "AlfLangActive" : ""} onClick={() => setLang('es')}>ES</button>
        </div>
    );

    return (
        <div className="Alf">
            {/* Helmet */}
            <Helmet>
                <html lang={Lang} />
                <title>{t.helmet.title}</title>
                <meta name="description" content={t.helmet.description} />
                <meta property="og:locale" content={Lang === 'es' ? 'es_US' : 'en_US'} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.emetya.com/alf" />
                <meta property="og:image" content="https://www.emetya.com/ahava-reel-1-poster.jpg" />
                <meta property="og:title" content={t.helmet.ogTitle} />
                <meta property="og:description" content={t.helmet.ogDescription} />
                <meta property="og:site_name" content="Emetya" />
                <meta name="keywords" content={t.helmet.keywords} />
            </Helmet>

            {/* Advertiser disclosure lives in the footer only. */}

            {/* AlfHeader */}
            <div className="AlfHeader">
                <span className="AlfHeaderBrand">
                    <img src={require(`../../Images/Logo.png`)} alt="Emetya" />
                    <h1>Emetya<span>{t.headerLabel}</span></h1>
                </span>
                <div className="AlfHeaderControls">
                    {LangToggleEl}
                    <button type="button" className="AlfHamburger" aria-label={t.menu.open} onClick={() => setMenuOpen(true)}>
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>
                </div>
            </div>

            {/* AlfMobileMenu */}
            {MenuOpen ?
                <div className="AlfMobileMenu">
                    <button type="button" className="AlfMobileMenuClose" aria-label={t.menu.close} onClick={() => setMenuOpen(false)}>×</button>
                    <div className="AlfMobileMenuBrand">
                        <img src={require(`../../Images/Logo.png`)} alt="Emetya" />
                        <h1>Emetya<span>{t.headerLabel}</span></h1>
                    </div>
                    <nav>
                        {t.menu.items.map((M, index) => (
                            <button type="button" key={index} onClick={() => menuNavigate(M.id)}>{M.label}</button>
                        ))}
                    </nav>
                    {LangToggleEl}
                </div>
                : null}

            {/* AlfHero */}
            <div className="AlfHero">
                <div className="AlfHeroInner">
                    <h1>{t.hero.h1}</h1>
                    <h2>{t.hero.sub}</h2>
                    <div className="AlfHeroFeature">
                        <div className="AlfHeroFeatureImg">
                            <img src={require(`../../AlfImages/hero-family.jpg`)} alt={t.hero.h1} />
                        </div>
                        <div className="AlfHeroFeatureBody">
                            <img className="AlfHeroAward" src={require(`../../AlfImages/ahava-award-usnews.png`)} alt={t.hero.award} />
                            <h3>{t.locations.title}</h3>
                            <span className="AlfHeroRule"></span>
                            <ul className="AlfHeroTrust">
                                <li>{t.hero.award}</li>
                                <li>{t.hero.chipLicensed}</li>
                                <li>{t.hero.chipFamily}</li>
                            </ul>
                            <div className="AlfHeroCtas">
                                <button className="AlfHeroCtaPrimary" onClick={() => scrollToForm()}>{t.hero.ctaPrimary}</button>
                                <button className="AlfHeroCtaSecondary" onClick={() => scrollToForm()}>{t.hero.ctaSecondary} &rsaquo;</button>
                            </div>
                            <h4>{t.hero.lastUpdated} {LastUpdated}</h4>
                        </div>
                    </div>
                </div>
            </div>

            {/* AlfRankedCard */}
            <div className="AlfRankedIntro">
                <h2 className="AlfSectionTitle">{t.ranked.introTitle}</h2>
                <p className="AlfSectionSub">{t.ranked.introSub}</p>
            </div>
            <div className="AlfRankedSection">
                <div className="AlfRankedCard">
                    <div className="AlfRankedBadge">
                        <img src={require(`../../Images/ranking.png`)} alt="" />
                        <h2>{t.ranked.badge}</h2>
                    </div>
                    <div className="AlfRankedTop">
                        <div className="AlfRankedTopMain">
                            <div className="AlfRankedTopLeft">
                                <h1>AHAVA</h1>
                                <h2>Senior Residences</h2>
                                <h3>{t.ranked.tagline}</h3>
                                <img src={require(`../../AlfImages/ahava-award-usnews.png`)} alt="U.S. News Best Memory Care 2025" />
                            </div>
                            <div className="AlfRankedMiddle">
                                <h1>AHAVA Senior Residences</h1>
                                {t.ranked.attributes.map((A, index) => (
                                    <h3 key={index}><img src={require(`../../Images/check-mark.png`)} alt="" />{A}</h3>
                                ))}
                            </div>
                        </div>
                        <div className="AlfRankedTopRight">
                            <div className="AlfRankedRating">
                                <h1>9.8</h1>
                                <span>
                                    <h2>{t.ranked.excellent}</h2>
                                    <span>
                                        <img src={require(`../../Images/star.png`)} alt="" />
                                        <img src={require(`../../Images/star.png`)} alt="" />
                                        <img src={require(`../../Images/star.png`)} alt="" />
                                        <img src={require(`../../Images/star.png`)} alt="" />
                                        <img src={require(`../../Images/star.png`)} alt="" />
                                    </span>
                                </span>
                            </div>
                            <h3>{t.ranked.ratedNo1}</h3>
                        </div>
                    </div>
                    <div className="AlfRankedPromo">
                        <img src={require(`../../Images/discount.png`)} alt="" />
                        <h2>{t.ranked.promo}</h2>
                    </div>
                    <div className="AlfRankedBook">
                        <span className="AlfRankedBookLeft">
                            <h2>{t.ranked.bookTitle}</h2>
                            <h3>{t.ranked.bookSub}</h3>
                        </span>
                        <button onClick={() => scrollToForm()}>{t.ranked.bookCta}</button>
                    </div>
                </div>
            </div>

            {/* AlfVideo */}
            <div className="AlfVideo" id="alf-videos" ref={VideoSection}>
                <div className="AlfVideoIntro">
                    <h2>{t.videos.title}</h2>
                    <p>{t.videos.text}</p>
                </div>
                {SoundOn ? null :
                    <button type="button" className="AlfVideoSound" onClick={() => { SoundRef.current = true; setSoundOn(true); const tr = VideoTrack.current; if (tr) tr.querySelectorAll('video').forEach((v) => { v.muted = false; v.volume = 1; }); }}>
                        &#128266; {t.videos.soundOn}
                    </button>}
                <div className="AlfVideoCarousel">
                    <button type="button" className="AlfVideoArrow AlfVideoArrowLeft" onClick={() => scrollVideos(-1)} aria-label={Lang === 'es' ? 'Anterior' : 'Previous'}>&#8249;</button>
                    <div className="AlfVideoGrid" ref={VideoTrack}>
                    {(Lang === 'es' ? [
                        { Src: "/ahava-es-nancy.mp4", Poster: "/ahava-reel-1-poster.jpg" },
                        { Src: "/ahava-es-interview.mp4", Poster: "/ahava-video-1274713247856592-poster.jpg" },
                        { Src: "/ahava-es-resident.mp4", Poster: "/ahava-video-981492880874294-poster.jpg" },
                        { Src: "/ahava-es-brand.mp4", Poster: "/ahava-video-924614260420388-poster.jpg" },
                    ] : [
                        { Src: "/ahava-reel-1-web.mp4", Poster: "/ahava-reel-1-poster.jpg" },
                        { Src: "/ahava-video-1274713247856592.mp4", Poster: "/ahava-video-1274713247856592-poster.jpg" },
                        { Src: "/ahava-video-981492880874294.mp4", Poster: "/ahava-video-981492880874294-poster.jpg" },
                        { Src: "/ahava-video-924614260420388.mp4", Poster: "/ahava-video-924614260420388-poster.jpg" },
                    ]).map((V, index) => (
                        <div className="AlfVideoPhone" key={index}>
                            <video controls playsInline muted preload={index === 0 ? "metadata" : "none"} poster={V.Poster} src={V.Src} />
                            <h4>{t.videos.captions[index]}</h4>
                        </div>
                    ))}
                    </div>
                    <button type="button" className="AlfVideoArrow AlfVideoArrowRight" onClick={() => scrollVideos(1)} aria-label={Lang === 'es' ? 'Siguiente' : 'Next'}>&#8250;</button>
                </div>
                <div className="AlfVideoQuotes">
                    {Testimonials.map((Q, index) => (
                        <div className="AlfVideoQuote" key={index}>
                            <h3>“{Q.Quote}”</h3>
                            <span>
                                <h4>{Q.Name}</h4>
                                <h5>{Q.Source}</h5>
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* AlfLocations */}
            <div className="AlfLocations" id="alf-locations">
                <h2 className="AlfSectionTitle">{t.locations.title}</h2>
                <p className="AlfSectionSub">{t.locations.sub}</p>
                <div className="AlfLocationsGrid">
                    {Locations.map((L, index) => (
                        <div className="AlfLocationCard" key={index}>
                            <div className="AlfLocationImg">
                                <img src={L.Img} alt={L.Name} loading="lazy" />
                            </div>
                            <div className="AlfLocationBody">
                                <h3>{L.Name}</h3>
                                <h4>{L.Address}</h4>
                                <div className="AlfLocationPrices">
                                    <div className="AlfLocationPrice">
                                        <h5>{t.locations.alLabel}</h5>
                                        <h6>{t.locations.from} {L.AlPrice}<span>{t.locations.mo}</span></h6>
                                    </div>
                                    <div className="AlfLocationPrice">
                                        <h5>{t.locations.mcLabel}</h5>
                                        <h6>{t.locations.from} {L.McPrice}<span>{t.locations.mo}</span></h6>
                                    </div>
                                </div>
                                <ul>
                                    {L.Bullets.map((B, bIndex) => (
                                        <li key={bIndex}><img src={require(`../../Images/check-mark.png`)} alt="" />{B}</li>
                                    ))}
                                </ul>
                                <button onClick={() => scrollToForm(L.FormValue)}>{t.locations.ctaPrefix}{L.FormValue}</button>
                                <h6 className="AlfLocationLicense">{L.License}</h6>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* AlfGallery */}
            <div className="AlfGallery" id="alf-gallery">
                <h2 className="AlfSectionTitle">{t.gallery.title}</h2>
                <p className="AlfSectionSub">{t.gallery.sub}</p>
                <div className="AlfGalleryGrid">
                    {GalleryImages.map((G, index) => (
                        <div className="AlfGalleryItem" key={index}>
                            <img src={G.Img} alt={G.Caption} loading="lazy" />
                            <h4>{G.Caption}</h4>
                        </div>
                    ))}
                </div>
            </div>

            {/* AlfWhy */}
            <div className="AlfWhy" id="alf-why">
                <h2 className="AlfSectionTitle">{t.why.title}</h2>
                <p className="AlfSectionSub">{t.why.sub}</p>
                <div className="AlfWhyGrid">
                    {WhyRows.map((W, index) => (
                        <div className="AlfWhyRow" key={index}>
                            <img src={require(`../../Images/check-mark.png`)} alt="" />
                            <span>
                                <h3>{W.Title}</h3>
                                <p>{W.Text}</p>
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* AlfFormSection */}
            <div className={`AlfFormSection${WizardOpen ? ' AlfFormSectionOpen' : ''}`} id="alf-form">
                {WizardOpen ?
                    <div className="AlfWizardTopBar">
                        <span className="AlfWizardBrand">
                            <img src={require(`../../Images/Logo.png`)} alt="Emetya" />
                            <h1>Emetya<span>{t.headerLabel}</span></h1>
                        </span>
                        <button type="button" className="AlfWizardClose" onClick={closeWizard} aria-label={t.menu.close}>&times;</button>
                    </div>
                    : null}
                <div className="AlfFormCard">
                    <div className="AlfFormImg">
                        <img src={require(`../../AlfImages/ahava-founder.jpg`)} alt={t.form.founderAlt} />
                        <div className="AlfFormImgCaption">
                            <h4>Ricki Kaneti</h4>
                            <h5>{t.form.founderRole}</h5>
                        </div>
                    </div>
                    <div className="AlfFormMain">
                        {Submitted ?
                            <div className="AlfFormThanks">
                                <img src={require(`../../Images/check-mark.png`)} alt="" />
                                <h2>{t.form.thanksTitle}</h2>
                                <p>{t.form.thanksText}</p>
                            </div>
                            :
                            <>
                                <h2>{t.form.title}</h2>
                                <p>{t.form.sub}</p>
                                <div className="AlfWizard" onClickCapture={() => { if (!WizardOpen) { setWizardOpen(true); document.body.style.overflow = 'hidden'; } }}>
                                    <div className="AlfWizardProgressBar">
                                        <span style={{ width: `${(WizardStep / 8) * 100}%` }}></span>
                                    </div>
                                    <div className="AlfWizardProgressMeta AlfWizardProgressMetaSolo">
                                        <h5>{t.form.stepOf.replace('{n}', String(WizardStep))}</h5>
                                    </div>

                                    {/* Step 1 - Who needs care */}
                                    {WizardStep === 1 ?
                                        <div className="AlfWizardStep">
                                            <h3>{t.form.who}</h3>
                                            <div className="AlfWizardOptions">
                                                {t.form.whoOptions.map((O, index) => (
                                                    <button type="button" key={index} className={`AlfWizardOption ${WhoNeedsCare === O.v ? "AlfWizardOptionSelected" : ""}`} onClick={() => pickWho(O.v)}>{O.l}</button>
                                                ))}
                                            </div>
                                        </div>
                                        : null}

                                    {/* Step 2 - Care type + memory loss */}
                                    {WizardStep === 2 ?
                                        <div className="AlfWizardStep">
                                            <h3>{t.form.careTypeQ}</h3>
                                            <div className="AlfWizardOptions">
                                                {t.form.careTypeOptions.map((O, index) => (
                                                    <button type="button" key={index} className={`AlfWizardOption ${CareType === O.v ? "AlfWizardOptionSelected" : ""}`} onClick={() => pickCareType(O.v)}>{O.l}</button>
                                                ))}
                                            </div>
                                            <h3 className={`AlfWizardSubQ ${CareType && !MemoryLoss ? "AlfWizardSubQMissing" : ""}`}>{t.form.memoryLossQ}</h3>
                                            <div className={`AlfWizardOptions AlfWizardOptionsRow ${CareType && !MemoryLoss ? "AlfWizardNeedsAnswer" : ""}`}>
                                                {t.form.memoryLossOptions.map((O, index) => (
                                                    <button type="button" key={index} className={`AlfWizardOption ${MemoryLoss === O.v ? "AlfWizardOptionSelected" : ""}`} onClick={() => pickMemoryLoss(O.v)}>{O.l}</button>
                                                ))}
                                            </div>
                                            {CareType && !MemoryLoss ? <p className="AlfWizardHint">{t.form.needsAnswer}</p> : null}
                                            {MemoryLoss && !CareType ? <p className="AlfWizardHint">{t.form.needsAnswer}</p> : null}
                                        </div>
                                        : null}

                                    {/* Step 3 - Care needs (multi-select) */}
                                    {WizardStep === 3 ?
                                        <div className="AlfWizardStep">
                                            <h3>{t.form.careNeedsQ}</h3>
                                            <div className="AlfWizardOptions AlfWizardOptionsGrid">
                                                {t.form.careNeedsOptions.map((O, index) => (
                                                    <button type="button" key={index} className={`AlfWizardOption ${CareNeeds.includes(O.v) ? "AlfWizardOptionSelected" : ""}`} onClick={() => pickCareNeed(O.v)}>
                                                        {O.l}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        : null}

                                    {/* Step 4 - Current living situation */}
                                    {WizardStep === 4 ?
                                        <div className="AlfWizardStep">
                                            <h3>{t.form.currentLivingQ}</h3>
                                            <div className="AlfWizardOptions">
                                                {t.form.currentLivingOptions.map((O, index) => (
                                                    <button type="button" key={index} className={`AlfWizardOption ${CurrentLiving === O.v ? "AlfWizardOptionSelected" : ""}`} onClick={() => pickCurrentLiving(O.v)}>{O.l}</button>
                                                ))}
                                            </div>
                                        </div>
                                        : null}

                                    {/* Step 5 - Move-in timeline */}
                                    {WizardStep === 5 ?
                                        <div className="AlfWizardStep">
                                            <h3>{t.form.timeline}</h3>
                                            <div className="AlfWizardOptions">
                                                {t.form.timelineOptions.map((O, index) => (
                                                    <button type="button" key={index} className={`AlfWizardOption ${Timeline === O.v ? "AlfWizardOptionSelected" : ""}`} onClick={() => pickTimeline(O.v)}>{O.l}</button>
                                                ))}
                                            </div>
                                        </div>
                                        : null}

                                    {/* Step 6 - Budget + payment source */}
                                    {WizardStep === 6 ?
                                        <div className="AlfWizardStep">
                                            <h3>{t.form.budgetQ}</h3>
                                            <div className="AlfWizardOptions AlfWizardOptionsRow">
                                                {t.form.budgetOptions.map((O, index) => (
                                                    <button type="button" key={index} className={`AlfWizardOption ${Budget === O.v ? "AlfWizardOptionSelected" : ""}`} onClick={() => pickBudget(O.v)}>{O.l}</button>
                                                ))}
                                            </div>
                                            <h3 className={`AlfWizardSubQ ${Budget && !PaymentSource ? "AlfWizardSubQMissing" : ""}`}>{t.form.paymentQ}</h3>
                                            <div className={`AlfWizardOptions ${Budget && !PaymentSource ? "AlfWizardNeedsAnswer" : ""}`}>
                                                {t.form.paymentOptions.map((O, index) => (
                                                    <button type="button" key={index} className={`AlfWizardOption ${PaymentSource === O.v ? "AlfWizardOptionSelected" : ""}`} onClick={() => pickPaymentSource(O.v)}>{O.l}</button>
                                                ))}
                                            </div>
                                            {Budget && !PaymentSource ? <p className="AlfWizardHint">{t.form.needsAnswer}</p> : null}
                                            {PaymentSource && !Budget ? <p className="AlfWizardHint">{t.form.needsAnswer}</p> : null}
                                        </div>
                                        : null}

                                    {/* Step 7 - Preferred location */}
                                    {WizardStep === 7 ?
                                        <div className="AlfWizardStep">
                                            <h3>{t.form.location}</h3>
                                            <div className="AlfWizardOptions">
                                                {t.form.locationOptions.map((O, index) => (
                                                    <button type="button" key={index} className={`AlfWizardOption ${PreferredLocation === O.v ? "AlfWizardOptionSelected" : ""}`} onClick={() => pickLocation(O.v)}>{O.l}</button>
                                                ))}
                                            </div>
                                        </div>
                                        : null}

                                    {/* Step 8 - Contact + care recipient + submit */}
                                    {WizardStep === 8 ?
                                        <div className="AlfWizardStep">
                                            <h3>{t.form.contactQ}</h3>
                                            <h4>{t.form.contactHint}</h4>
                                            <form onSubmit={handleSubmit}>
                                                <label>
                                                    {t.form.fullName}
                                                    <input type="text" name="Full_Name" placeholder={t.form.fullNamePh} required />
                                                </label>
                                                <label>
                                                    {t.form.phone}
                                                    <input type="tel" name="Phone" placeholder={t.form.phonePh} required />
                                                </label>
                                                <label>
                                                    {t.form.email}
                                                    <input type="email" name="Email" placeholder={t.form.emailPh} required />
                                                </label>
                                                <div className="AlfFormRow">
                                                    <label>
                                                        {t.form.recipientName}
                                                        <input type="text" name="Recipient_Name" placeholder={t.form.recipientNamePh} />
                                                    </label>
                                                    <label>
                                                        {t.form.recipientAge}
                                                        <input type="number" name="Recipient_Age" placeholder={t.form.recipientAgePh} min="1" max="120" inputMode="numeric" />
                                                    </label>
                                                </div>
                                                <input type="hidden" name="Message" value={ComposedMessage} />
                                                <input type="hidden" name="Who_Needs_Care" value={WhoNeedsCare} />
                                                <input type="hidden" name="Preferred_Location" value={PreferredLocation} />
                                                <input type="hidden" name="Care_Type" value={CareType} />
                                                <input type="hidden" name="Timeline" value={Timeline} />
                                                <input type="hidden" name="Budget" value={Budget} />
                                                <input type="hidden" name="Payment_Source" value={PaymentSource} />
                                                <input type="hidden" name="Care_Needs" value={CareNeedsString} />
                                                <input type="hidden" name="Current_Living" value={CurrentLiving} />
                                                <input type="hidden" name="Memory_Loss" value={MemoryLoss} />
                                                <input type="hidden" name="Contact_Relationship" value={ContactRelationship} />
                                                <input type="hidden" name="Advisor_Email" value={AdvisorEmail} />
                                                <input type="hidden" name="Medicaid_Flag" value={MedicaidFlag} />
                                                <input type="hidden" name="Lead_City" value={Geo.city} />
                                                <input type="hidden" name="Lead_State" value={Geo.state} />
                                                <input type="hidden" name="Lead_Zip" value={Geo.zip} />
                                                <input type="hidden" name="Lead_Source" value={"emetya.com/alf" + window.location.search} />
                                                <input type="hidden" name="Country" value="USA" />
                                                <input type="hidden" name="Company_From" value="Emetya.com | ALF | AHAVA Senior Residences" />
                                                <input type="hidden" name="Company" value="Emetya.com | ALF | AHAVA Senior Residences" />
                                                <button type="submit" disabled={Sending}>{Sending ? t.form.sending : t.form.submit}</button>
                                                {SendError ?
                                                    <h5 className="AlfFormError">{t.form.error}</h5>
                                                    : null}
                                                <h6>{t.form.consent}</h6>
                                            </form>
                                        </div>
                                        : null}

                                    {WizardStep > 1 ?
                                        <div className="AlfWizardNav">
                                            <button type="button" className="AlfWizardBack" onClick={() => { setWentBack(true); setWizardStep(WizardStep - 1); }}>&lsaquo; {t.form.back}</button>
                                        </div>
                                        : null}
                                </div>
                            </>
                        }
                    </div>
                </div>
            </div>

            {/* AlfFaq */}
            <div className="AlfFaq" id="alf-faq">
                <h2 className="AlfSectionTitle">{t.faq.title}</h2>
                <div className="AlfFaqList">
                    {Faqs.map((F, index) => (
                        <div className={`AlfFaqItem ${OpenFaq === index ? "AlfFaqItemOpen" : ""}`} key={index}>
                            <button type="button" onClick={() => setOpenFaq(OpenFaq === index ? null : index)}>
                                <h3>{F.Question}</h3>
                                <span>{OpenFaq === index ? "–" : "+"}</span>
                            </button>
                            {OpenFaq === index ?
                                <p>{F.Answer}</p>
                                : null}
                        </div>
                    ))}
                </div>
            </div>

            {/* AlfFooter */}
            <div className="AlfFooter">
                <h6>{t.footer.p1}</h6>
                <h6>{t.footer.p2}</h6>
                <div className="AlfFooterLogo">
                    <img src={require(`../../Images/Logo.png`)} alt="" />
                    <h1>Emetya<span>{t.headerLabel}</span></h1>
                </div>
                <h5>{t.footer.rights}</h5>
                <span>
                    <a href="https://www.emetya.com/privacy-policy">{t.footer.privacy}</a>
                    <a href="https://www.emetya.com/terms-of-use">{t.footer.terms}</a>
                </span>
            </div>

            {/* AlfProof - social proof nudge */}
            {Proof && !ProofClosed && !Submitted ?
                <div className="AlfProof">
                    <button type="button" className="AlfProofClose" onClick={() => { setProof(false); setProofClosed(true); }} aria-label={t.menu.close}>&times;</button>
                    <div className="AlfProofDot"></div>
                    <div className="AlfProofBody">
                        <h4>{t.proof.title}</h4>
                        <p>{t.proof.text}</p>
                    </div>
                </div>
                : null}

            {/* AlfStickyBar */}
            {Submitted ? null :
                <div className="AlfStickyBar">
                    <button onClick={() => scrollToForm()}>{t.sticky}</button>
                </div>
            }
        </div>
    )
}
